/***
 * Copyright (c) Omnissa, LLC. All rights reserved.
 * This product is protected by copyright and intellectual property laws in the
 * United States and other countries as well as by international treaties.
 * -- Omnissa Restricted
 *
 * @format
 **/

// IWA Client only for now, web is possible
// Client must be opened by extension, and should not send the first message
// When this service loaded, it should check whether it should connect to extension, thus extra interface of connect with URL check is needed.
// in PoC uses window.sdkLaunchSessionId from url arg, as the first layer check along with allowedPeerAddresss.

import { SDKRawEvent } from "../event-helper/event-helper";
import { EventExchangeBase, SDKReceivedRawRequest } from "./event-exchange.base";
import { Logger } from "../logger";

interface RequestMessage{
   msgId: string;
   message: SDKRawEvent;
   isKeepAlive?: boolean;
};

export class EventExchangeLongLivedMessageExtension extends EventExchangeBase {
   private ports: Map<string, chrome.runtime.Port> = new Map();
   private callbacks: Map<string, Function> = new Map();
   private messagesToBeSent: Map<string, Array<RequestMessage>> = new Map();
   private keepAliveIntervals: Map<string, any> = new Map();//TODO: need to change tsconfig for strong type
   private KEEP_ALIVE_INTERVAL_MS = 20000; // 20 seconds
   private ALARM_NAME = 'sdk-keep-alive-alarm';
   private isAlarmActive = false;
   
   constructor(
      allowedPeerAddresss: Array<string>|null,
      getTrustOriginList: (string) => Promise<Array<string>>,
      isTrustSyncMessage?: (SDKRawEvent) => boolean
   ) {
      super(allowedPeerAddresss, getTrustOriginList, isTrustSyncMessage);
      
      // Only set up the alarm listener, but don't create the alarm yet
      if (chrome?.alarms) {
         try {
            // Set up listener for the alarm
            chrome.alarms.onAlarm.addListener((alarm) => {
               if (alarm.name === this.ALARM_NAME) {
                  this.sendKeepAliveToAllPorts();
               }
            });
            
            Logger.info("Initialized alarm system for keep-alive mechanism");
         } catch (error) {
            Logger.error(`Error setting up alarm listener: ${error}`);
         }
      } else {
         Logger.warning('chrome.alarms API is not available, using only standard keep-alive mechanism');
      }
      
      if (chrome?.runtime?.onConnectExternal) {
         chrome.runtime.onConnectExternal.addListener((port) => {
            const sender = port.sender;
            const senderOrigin = this.getOrigin(sender.url);
            if (!senderOrigin) {
               return false;
            }
            const key = port.name;
            this.ports.set(key, port);
            Logger.debug(`Port ${key} is opened by sender: ${sender.url}  origin: ${senderOrigin}`);
            
            // Start the regular keep-alive interval for this port
            this.startKeepAliveInterval(key, port);
            
            // Check if this is the first port - if so, start the alarm
            if (this.ports.size === 1) {
               this.startAlarm();
            }
            
            let pendingMessages = this.messagesToBeSent.get(key);
            if(pendingMessages){
               setTimeout(()=>{//asynchronizely sent cached pending message to channel.
                  for(const pendingMessage of pendingMessages) {
                     this.sendMessageOnPort(port, pendingMessage);
                  }
               });
            }
            return this.addListenerForPort(port);
         });
      }
   }

   // Start the alarm if not already active
   private startAlarm() {
      if (!chrome?.alarms || this.isAlarmActive) {
         return;
      }
      
      try {
         // Create an alarm that fires every 30 seconds (Chrome 120+ supports this)
         try {
            chrome.alarms.create(this.ALARM_NAME, { periodInMinutes: 0.5 });
            this.isAlarmActive = true;
            Logger.info(`Started chrome alarm for keep-alive mechanism: ${this.ALARM_NAME} (30s interval)`);
         } catch (error) {
            Logger.error(`Failed to create alarm: ${error}`);
            // Try with the standard 1-minute interval as fallback
            try {
               chrome.alarms.create(this.ALARM_NAME, { periodInMinutes: 1 });
               this.isAlarmActive = true;
               Logger.info(`Started fallback chrome alarm: ${this.ALARM_NAME} (60s interval)`);
            } catch (error2) {
               Logger.error(`Failed to create fallback alarm: ${error2}`);
            }
         }
      } catch (error) {
         Logger.error(`Error starting alarm: ${error}`);
      }
   }

   // Stop the alarm if it's active
   private stopAlarm() {
      if (!chrome?.alarms || !this.isAlarmActive) {
         return;
      }
      
      try {
         try {
            const wasCleared = chrome.alarms.clear(this.ALARM_NAME);
            if (typeof wasCleared === 'boolean') {
               Logger.info(`Stopped chrome alarm ${this.ALARM_NAME}: ${wasCleared ? 'success' : 'not found'}`);
            } else {
               Logger.info(`Requested to stop chrome alarm: ${this.ALARM_NAME}`);
            }
            this.isAlarmActive = false;
         } catch (error) {
            Logger.error(`Error clearing alarm: ${error}`);
         }
      } catch (error) {
         Logger.error(`Error stopping alarm: ${error}`);
      }
   }

   public disconnectPort = (portId: string, reason?: string) => {
      const port = this.ports.get(portId);
      if (port) {
         Logger.debug(`Disconnet port ${port.name}, portId : ${portId} since ${reason || "manual disconnection"}`);
         port.disconnect();
         this.ports.delete(portId);
         
         // Clear keep-alive interval
         this.clearKeepAliveInterval(portId);
         
         // If this was the last port, stop the alarm
         if (this.ports.size === 0) {
            Logger.info("All ports disconnected, stopping keep-alive alarm");
            this.stopAlarm();
         }
         
         this._emitDisconnect(portId, reason || "manual disconnection");
      }
   };

   private startKeepAliveInterval(portId: string, port: any) {
      // Clear any existing interval first
      this.clearKeepAliveInterval(portId);
      
      // Set new interval
      const intervalId = setInterval(() => {
         if (this.ports.has(portId)) {
            // Send a simple keep-alive message
            const keepAliveMessage: RequestMessage = {
               msgId: `keepalive-${Date.now()}`,
               message: { type: "KEEP_ALIVE", data: { timestamp: Date.now(), source: "extension" } } as SDKRawEvent,
               isKeepAlive: true
            };
            port.postMessage(keepAliveMessage);
            Logger.debug(`Sent keep-alive message to port ${portId}`);
         } else {
            this.clearKeepAliveInterval(portId);
         }
      }, this.KEEP_ALIVE_INTERVAL_MS);
      
      this.keepAliveIntervals.set(portId, intervalId);
      Logger.info(`Started keep-alive interval for port ${portId} (${this.KEEP_ALIVE_INTERVAL_MS}ms)`);
   }

   private clearKeepAliveInterval(portId: string) {
      const interval = this.keepAliveIntervals.get(portId);
      if (interval) {
         clearInterval(interval);
         this.keepAliveIntervals.delete(portId);
         Logger.info(`Cleared keep-alive interval for port ${portId}`);
      }
   }

   private sendKeepAliveToAllPorts() {
      if (this.ports.size === 0) {
         Logger.info('No ports connected, stopping alarm-based keep-alive');
         this.stopAlarm();
         return;
      }
      
      Logger.debug(`Sending alarm-based keep-alive to ${this.ports.size} connected ports (30s interval)`);
      
      this.ports.forEach((port, portId) => {
         if (port) {
            const keepAliveMessage: RequestMessage = {
               msgId: `alarm-keepalive-${Date.now()}`,
               message: { 
                  type: "KEEP_ALIVE", 
                  data: { 
                     timestamp: Date.now(), 
                     source: "extension-alarm-30s" 
                  } 
               } as SDKRawEvent,
               isKeepAlive: true
            };
            
            try {
               port.postMessage(keepAliveMessage);
               Logger.debug(`Sent alarm-based keep-alive message to port ${portId}`);
            } catch (error) {
               Logger.error(`Failed to send alarm-based keep-alive to port ${portId}, removing from active ports: ${error}`);
               this.disconnectPort(portId, "Failed to send alarm-based keep-alive");
            }
         }
      });
   }

   private sendMessageOnPort(port: any, message: RequestMessage){
      port.postMessage(message);
      Logger.trace(
         `Sent message ${JSON.stringify(message)} on port ${JSON.stringify(port)}`
      );
   }

   public sendMessage = (peerAddress: string, data: SDKRawEvent, portId: string|undefined = undefined): Promise<SDKRawEvent> => {
      Logger.trace(`requesting to send to ${peerAddress} with ${portId}`);
      let port: any;
      const key = portId ? portId : location.host;//here use self address instead of peer Address, since it's abnormal for non-GA client installed
      return new Promise((resolve, reject) => {
         try {
            const msgId = Math.random().toString(36);
            this.callbacks.set(msgId, (response: SDKRawEvent) => {
               resolve(response);
            });
            let messageToBeSent:RequestMessage = { msgId: msgId, message: data };
            if (!this.ports.has(key)) {
               // instead of connect directly, launch the IWA for revocation connect per API requested.
               const extensionId = location.host;
               // TODO: change URL and add no-install check after API of isolated-app:// is supported with launch hander.
               const launchUrl = `web+sdk://sdk/?extensionId=${extensionId}&portKey=${key}`;
               chrome.tabs.create({
                  url: launchUrl,
                  active: true
               }).then((tab)=>{
                  Logger.debug("IWA tab created, waiting connection establish: " + launchUrl);
               }).catch(reject);
               // Record the sending message and send when the connect established. A queue should be used
               // the the message should be stored with the key.
               let existingPendingMessages = this.messagesToBeSent.get(key) || new Array<RequestMessage>();
               existingPendingMessages.push(messageToBeSent);
               this.messagesToBeSent.set(key, existingPendingMessages);
            } else {
               port = this.ports.get(key);
               Logger.debug(`Use existed port ${port.name}`);
               this.sendMessageOnPort(port, messageToBeSent);
            }
         } catch (e) {
            this.ports.delete(key);
            Logger.debug(`Failed to open port with app ${peerAddress}. Error in chrome.runtime.connect`);
            reject(e);
         }
      });
   };

   public cleanup() {
      // Always stop the alarm during cleanup
      if (chrome?.alarms && this.isAlarmActive) {
         this.stopAlarm();
      }
      
      // Disconnect all ports
      this.ports.forEach((_, portId) => {
         this.disconnectPort(portId, "Service cleanup");
      });
   }

   private addListenerForPort = (port: any): boolean => {
      Logger.log("connected then listen for port" + port.name);
      port.onMessage.addListener((data, port) => {
         const message = data.message;
         const msgId = data.msgId;
         
         // Handle keep-alive messages with improved logging
         if (data.isKeepAlive) {
            Logger.debug(`Received keep-alive message from port ${port.name}`);
            return;
         }
         
         Logger.trace(`Receive data ${JSON.stringify(data)} from port ${JSON.stringify(port)}`);
         
         if (data.isResponse) {
            const callback = this.callbacks.get(msgId);
            if (typeof callback === "function") {
               callback(message);
               Logger.debug(`Message ${msgId} is resolved.`);
            }
            this.callbacks.delete(msgId);
         } else {
            const msgSenderOrigin = this.getOrigin(port.sender.url);

            if (!msgSenderOrigin) {
               return false;
            }
            this.getAllowedPeerAddress(msgSenderOrigin, message).then((matchedPeerAddress) => {
               if (!matchedPeerAddress) {
                  Logger.warning("ignore message from " + msgSenderOrigin);
                  return false;
               }

               // double check sender in code again
               const request: SDKReceivedRawRequest = {
                  request: message as SDKRawEvent,
                  sendResponse: (response: SDKRawEvent) => {
                     port.postMessage({ isResponse: true, msgId: msgId, message: response });
                  },
                  peerAddress: matchedPeerAddress
               };
               this.message$.next(request);
            });
         }
      });
      port.onDisconnect.addListener((port) => {
         let portId = port.name;
         Logger.debug(`Port disconnected since receiving end does not exist for port with id ${portId}`);
         this.ports.delete(portId);
         
         // Clear keep-alive interval
         this.clearKeepAliveInterval(portId);
         
         // If this was the last port, stop the alarm
         if (this.ports.size === 0) {
            Logger.info("All ports disconnected, stopping keep-alive alarm");
            this.stopAlarm();
         }
         
         this._emitDisconnect(portId, "Client disconnected unexpectedly");
      });
      return true;
   };
}
